import { useEffect, useRef } from "react";

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
}

export function TurnstileWidget({ onVerify }: TurnstileWidgetProps) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let widgetId: string | undefined;
    let widgetSize: "normal" | "compact" | undefined;
    let resizeFrame: number | undefined;
    let disposed = false;

    const render = () => {
      if (disposed || !container.current || !window.turnstile) return;
      const size = container.current.getBoundingClientRect().width < 300 ? "compact" : "normal";
      if (widgetId !== undefined && widgetSize === size) return;
      if (widgetId !== undefined) {
        window.turnstile.remove(widgetId);
        onVerify("");
      }
      widgetSize = size;
      widgetId = window.turnstile.render(container.current, {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
        theme: "light",
        size,
        language: "tr",
        callback: onVerify,
        "expired-callback": () => onVerify(""),
        "error-callback": () => onVerify("")
      });
    };

    let script = document.querySelector<HTMLScriptElement>("script[data-turnstile]");
    if (window.turnstile) {
      render();
    } else if (script) {
      script.addEventListener("load", render, { once: true });
    } else {
      script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = "true";
      script.addEventListener("load", render, { once: true });
      document.head.append(script);
    }

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(() => {
              resizeFrame = undefined;
              render();
            });
          });
    if (container.current) observer?.observe(container.current);

    return () => {
      disposed = true;
      observer?.disconnect();
      if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
      script?.removeEventListener("load", render);
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onVerify]);

  return <div className="turnstile-slot" ref={container} aria-label="İnsan doğrulaması" />;
}
